import React, { Component } from "react";
import { Button, Alert } from "reactstrap";
import moment from "moment";

import { LoadingModal } from "./LoadingModal";
import { FetchData } from "./utils/DataFetch";
import { GetCookie, Timeout } from "./utils/Common";
import { TranslationBoard } from "./TranslationBoard";
import { TranslationForm, toTranslationModel } from "./TranslationForm";

import "./Translation.css";

export class Translation extends Component {
  static displayName = Translation.name;

  constructor(props) {
    super(props);
    this.state = {
      translationList: [],
      workflow: [],
      fetchingData: false,
      modal: false,
      model: toTranslationModel(null),
      alert: { color: "info", message: "", isOpen: false },
      announcement: "",
      searchModel: {
        JobNum: "",
        StartDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
        EndDate: "",
        ClientAbbrv: "",
        IncludeDeleted: false,
      },
    };
    this.user = JSON.parse(GetCookie("user"));

    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.populateTranslationData = this.populateTranslationData.bind(this);
    this.setModal = this.setModal.bind(this);
    this.openNew = this.openNew.bind(this);
    this.openTranslation = this.openTranslation.bind(this);
    this.moveTranslation = this.moveTranslation.bind(this);
    this.handleSaveEventSuccess = this.handleSaveEventSuccess.bind(this);
  }

  componentDidMount() {
    FetchData("/gettranslationworkflow", "GET", this.user.token).then((data) => {
      if (Array.isArray(data)) this.setState({ workflow: data });
      else this.showAlert("Could not load the translation workflow.", "danger");
    });
    this.populateTranslationData();
  }

  showAlert(message, color) {
    this.setState({ alert: { color, message, isOpen: true } }, () => {
      window.setTimeout(() => {
        this.setState((prev) => ({ alert: { ...prev.alert, isOpen: false } }));
      }, Timeout(message));
    });
  }

  setModal() {
    this.setState((prev) => ({ modal: !prev.modal }));
  }

  openNew() {
    this.setState({ model: toTranslationModel(null), modal: true });
  }

  openTranslation(job) {
    this.setState({ model: toTranslationModel(job), modal: true });
  }

  handleSaveEventSuccess(alert) {
    this.setState({ modal: alert.modal, announcement: alert.message }, () => {
      this.showAlert(alert.message, alert.color);
      this.populateTranslationData();
    });
  }

  /**
   * Moves the card immediately, then asks the server. A rejected move (e.g. someone
   * else moved it first) puts the card back and reloads the board.
   */
  moveTranslation(job, toWorkFlowId) {
    const fromWorkFlowId = job.workFlowId;
    const setWorkflow = (value) =>
      this.setState((prev) => ({
        translationList: prev.translationList.map((t) =>
          t.jobId === job.jobId ? { ...t, workFlowId: value } : t,
        ),
      }));

    setWorkflow(toWorkFlowId);

    FetchData("/movetranslation", "POST", this.user.token, {
      JobId: job.jobId,
      FromWorkFlowId: fromWorkFlowId,
      ToWorkFlowId: toWorkFlowId,
    }).then((data) => {
      if (data?.message === "Success") {
        this.setState({
          announcement: `Translation #${job.jobNum} moved to ${toWorkFlowId}.`,
        });
      } else {
        setWorkflow(fromWorkFlowId);
        const message = data?.message ?? `Could not move Translation #${job.jobNum}.`;
        this.setState({ announcement: message });
        this.showAlert(message, "warning");
        this.populateTranslationData();
      }
    });
  }

  handleSearchChange(e) {
    const { name, type, checked, value } = e.target;
    const model = { ...this.state.searchModel };
    model[name] = type === "checkbox" ? checked : value;

    if (type === "checkbox") {
      this.setState({ searchModel: model }, this.populateTranslationData);
    } else {
      this.setState({ searchModel: model });
    }
  }

  handleKeyDown(e) {
    if (e.key === "Enter") {
      this.populateTranslationData();
    }
  }

  async populateTranslationData() {
    this.setState({ fetchingData: true });

    const { searchModel } = this.state;
    const filters = {
      ...searchModel,
      JobNum: searchModel.JobNum === "" ? null : Number(searchModel.JobNum),
      StartDate: searchModel.StartDate || null,
      EndDate: searchModel.EndDate || null,
    };

    FetchData("/gettranslations", "POST", this.user.token, filters).then(
      (data) => {
        this.setState({
          translationList: Array.isArray(data) ? data : [],
          fetchingData: false,
        });
      },
    );
  }

  render() {
    const { searchModel, translationList, workflow } = this.state;
    const count = translationList.length;

    return (
      <div className="tr-module">
        <div className="tr-sr-only" aria-live="polite" role="status">
          {this.state.announcement}
        </div>

        <div className="tr-page-header">
          <div>
            <h1 className="tr-page-title">Translation</h1>
            <p className="tr-page-subtitle">
              {count} {count === 1 ? "translation" : "translations"}
            </p>
          </div>
          <Button color="primary" onClick={this.openNew}>
            + New translation
          </Button>
        </div>

        <Alert color={this.state.alert.color} isOpen={this.state.alert.isOpen}>
          {this.state.alert.message}
        </Alert>

        <div className="tr-filters">
          <div className="tr-field">
            <label className="form-label" htmlFor="tr-filter-num">
              Translation #
            </label>
            <input
              id="tr-filter-num"
              name="JobNum"
              type="number"
              className="form-control form-control-sm"
              value={searchModel.JobNum}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="tr-field">
            <label className="form-label" htmlFor="tr-filter-from">
              From
            </label>
            <input
              id="tr-filter-from"
              name="StartDate"
              type="date"
              className="form-control form-control-sm"
              value={searchModel.StartDate}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="tr-field">
            <label className="form-label" htmlFor="tr-filter-to">
              To
            </label>
            <input
              id="tr-filter-to"
              name="EndDate"
              type="date"
              className="form-control form-control-sm"
              value={searchModel.EndDate}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="tr-field">
            <label className="form-label" htmlFor="tr-filter-client">
              Client abbrv
            </label>
            <input
              id="tr-filter-client"
              name="ClientAbbrv"
              type="text"
              className="form-control form-control-sm"
              value={searchModel.ClientAbbrv}
              onChange={this.handleSearchChange}
              onKeyDown={this.handleKeyDown}
            />
          </div>
          <div className="tr-filter-actions">
            <div className="form-check form-switch">
              <input
                id="tr-filter-deleted"
                name="IncludeDeleted"
                type="checkbox"
                role="switch"
                className="form-check-input"
                checked={searchModel.IncludeDeleted}
                onChange={this.handleSearchChange}
              />
              <label className="form-check-label" htmlFor="tr-filter-deleted">
                Show deleted
              </label>
            </div>
            <Button color="secondary" outline size="sm" onClick={this.populateTranslationData}>
              Search
            </Button>
          </div>
        </div>

        <LoadingModal fetchingData={this.state.fetchingData} />

        {this.state.modal && (
          <TranslationForm
            model={this.state.model}
            modal={this.state.modal}
            workflow={workflow}
            setModal={this.setModal}
            handleSaveEventSuccess={this.handleSaveEventSuccess}
          />
        )}

        <TranslationBoard
          columns={workflow}
          translations={translationList}
          onOpen={this.openTranslation}
          onMove={this.moveTranslation}
        />
      </div>
    );
  }
}
